import React from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { AppModal as Modal } from '../atoms/AppModal'
import { Text } from '../atoms'
import { Theme } from '@theme'
import { getCommonStyles } from '@utils'
import { useLanguage } from '@context'
import type { TranslateTextLanguage } from '@hooks'

interface TranslateLangPickerProps {
  isVisible: boolean
  setIsVisible: (visible: boolean) => void
  /** Currently active translation language, or null if showing original. */
  currentLang: TranslateTextLanguage | null
  onPick: (lang: TranslateTextLanguage) => void
  /** Revert to original. Shown as the first item only when currentLang is set. */
  onRevert: () => void
  /**
   * Optional anchor in screen coords. When provided the modal renders as
   * a small popover next to the trigger; otherwise it centres on screen.
   */
  position?: { x: number; y: number }
}

interface LangOption {
  code: TranslateTextLanguage
  flag: string
  /** Translation key for the label, e.g. "translate.lang.fr" → "Français". */
  labelKey: string
}

const OPTIONS: LangOption[] = [
  { code: 'fr', flag: '🇫🇷', labelKey: 'translate.lang.fr' },
  { code: 'en', flag: '🇬🇧', labelKey: 'translate.lang.en' },
  { code: 'es', flag: '🇪🇸', labelKey: 'translate.lang.es' },
]

export const TranslateLangPicker: React.FC<TranslateLangPickerProps> = ({
  isVisible,
  setIsVisible,
  currentLang,
  onPick,
  onRevert,
  position,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const commonStyles = getCommonStyles(theme)
  const styles = getStyles(theme)

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="fade"
      onRequestClose={() => setIsVisible(false)}
    >
      <TouchableOpacity
        style={[styles.backdrop, position !== undefined && { backgroundColor: 'transparent' }]}
        onPress={() => setIsVisible(false)}
        activeOpacity={1}
      >
        <View
          style={[
            styles.card,
            commonStyles.shadow,
            position !== undefined && {
              position: 'absolute',
              left: position.x,
              top: position.y,
            },
          ]}
        >
          {currentLang !== null && (
            <TouchableOpacity
              style={[styles.row, styles.rowDivider]}
              onPress={() => {
                onRevert()
                setIsVisible(false)
              }}
            >
              <Text type="body">{t('translate.original')}</Text>
            </TouchableOpacity>
          )}
          {OPTIONS.map((opt, i) => {
            const active = opt.code === currentLang
            return (
              <TouchableOpacity
                key={opt.code}
                style={[
                  styles.row,
                  i < OPTIONS.length - 1 && styles.rowDivider,
                  active && { backgroundColor: theme.colors.primary + '12' },
                ]}
                onPress={() => {
                  onPick(opt.code)
                  setIsVisible(false)
                }}
              >
                <Text type="body" style={styles.flag}>
                  {opt.flag}
                </Text>
                <Text
                  type={active ? 'bodyBold' : 'body'}
                  color={active ? theme.colors.primary : theme.colors.text}
                >
                  {t(opt.labelKey)}
                </Text>
              </TouchableOpacity>
            )
          })}
        </View>
      </TouchableOpacity>
    </Modal>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.3)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    card: {
      backgroundColor: theme.colors.white,
      borderRadius: 12,
      paddingVertical: 4,
      minWidth: 180,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
    },
    rowDivider: {
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.grey20,
    },
    flag: {
      fontSize: 20,
    },
  })
